import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { getDashboardStats } from '../api/dashboard';
import { AppShell } from '../components/AppShell';
import { Icon } from '../components/Icon';
import { StatCard } from '../components/StatCard';
import { Button, Card, CardBody, CardHeader, Skeleton } from '../components/ui';
import { useRecentActivity } from '../features/activity/hooks/useRecentActivity';
import { useAuth } from '../features/auth/AuthProvider';
import styles from './DashboardPage.module.scss';

export function DashboardPage() {
  const { user } = useAuth();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: getDashboardStats,
  });

  const { data: activity, isLoading: activityLoading } = useRecentActivity();

  const today = new Date().toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'uk-UA', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  function timeAgo(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime();
    const hours = Math.floor(diff / 3600000);
    if (hours < 1) return t('dashboard.justNow');
    if (hours < 24) return t('dashboard.hoursAgo', { count: hours });
    const days = Math.floor(hours / 24);
    return t('dashboard.daysAgo', { count: days });
  }

  function getInitials(name: string | null | undefined): string {
    if (!name) return '?';
    return name
      .split(' ')
      .map((w) => w[0])
      .join('')
      .slice(0, 2);
  }

  return (
    <AppShell>
      <div className={styles.page}>
        <div className={styles.heading}>
          <h2 className={styles.greeting}>{t('dashboard.greeting', { name: user?.full_name ?? 'WF' })}</h2>
          <p className={styles.date}>{today}</p>
        </div>

        <div className={styles.statsGrid}>
          {statsLoading ? (
            Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} width="100%" height={112} />
            ))
          ) : (
            <>
              <StatCard
                label={t('dashboard.totalPatients')}
                value={stats?.total_patients ?? 0}
                icon={<Icon name="people" size={18} />}
              />
              <StatCard
                label={t('dashboard.activeTreatments')}
                value={stats?.active_treatments ?? 0}
                icon={<Icon name="trending-up" size={18} />}
              />
              <StatCard
                label={t('dashboard.pendingDeliveries')}
                value={stats?.pending_deliveries ?? 0}
                icon={<Icon name="shipping" size={18} />}
              />
              <StatCard
                label={t('dashboard.pendingPayments')}
                value={stats?.pending_payments ?? 0}
                icon={<Icon name="payment" size={18} />}
              />
            </>
          )}
        </div>

        <div className={styles.bottomGrid}>
          <Card padding="none">
            <CardHeader title={t('dashboard.recentActivity')} />
            <CardBody className={styles.activityBody}>
              {activityLoading ? (
                <div className={styles.activityList}>
                  {Array.from({ length: 4 }, (_, i) => (
                    <Skeleton key={i} width="100%" height={48} />
                  ))}
                </div>
              ) : (
                <div className={styles.activityList}>
                  {(activity ?? []).map((item) => {
                    const clickable = Boolean(item.patient_id);
                    return (
                      <div
                        key={item.id}
                        className={`${styles.activityItem} ${clickable ? styles.activityItemClickable : ''}`}
                        onClick={clickable ? () => navigate(`/clients/${item.patient_id}`) : undefined}
                        role={clickable ? 'button' : undefined}
                        tabIndex={clickable ? 0 : undefined}
                      >
                        <div className={styles.activityAvatar}>
                          {getInitials(item.patient_name ?? item.admin_name)}
                        </div>
                        <div className={styles.activityContent}>
                          <div className={styles.activityName}>{item.admin_name ?? t('common.system')}</div>
                          <div className={styles.activityAction}>{item.summary}</div>
                        </div>
                        <span className={styles.activityTime}>{timeAgo(item.created_at)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardBody>
          </Card>

          <Card padding="none">
            <CardHeader title={t('dashboard.quickActions')} />
            <CardBody className={styles.quickActionsBody}>
              <Button variant="primary" fullWidth onClick={() => navigate('/clients')}>
                {t('dashboard.viewAllClients')}
              </Button>
              <Button variant="secondary" fullWidth>
                {t('dashboard.addNewPatient')}
              </Button>
            </CardBody>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}
